import React from 'react';
import { Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import { useTranslation } from '../../providers/TranslationProvider';
import { debug } from '../../utils/logger';

const styles = StyleSheet.create({
  container: {
    gap: 6,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Colors.siteListUnconfirmedBorder,
    backgroundColor: Colors.siteListUnconfirmedBg,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: Colors.siteListUnconfirmedText,
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.mediumGray,
  },
  phone: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.darkGreen,
  },
});

export interface UnconfirmedHoursCardProps {
  phone?: string;
}

// Skips any leading non-digit label (e.g. "Tel: ", "Office ") to find where the number actually
// starts, then takes the run of digits/formatting characters (and an optional leading '+') from
// there, stopping at the first character that's neither -- e.g. the space before a written-out
// extension ("(212) 555-1234 ext. 5"). A global strip-and-concatenate would instead fold the
// extension's digits onto the end of the subscriber number and dial a wrong, eleven-digit number.
function sanitizePhoneForDialing(phone: string): string {
  const numberStart = phone.search(/\+?\d/);
  if (numberStart === -1) return '';
  const leadingRun = phone.slice(numberStart).match(/^\+?[\d\s().-]*/)?.[0] ?? '';
  return leadingRun.replace(/[^\d+]/g, '');
}

export function UnconfirmedHoursCard({ phone }: UnconfirmedHoursCardProps) {
  const { t } = useTranslation();

  return (
    <View style={styles.container} testID="unconfirmed-hours-card">
      <AppText style={styles.title}>{t('site_list_unconfirmed_title')}</AppText>
      <AppText style={styles.body}>{t('site_list_unconfirmed_body')}</AppText>
      {phone && (
        <TouchableOpacity
          onPress={() => {
            Linking.openURL(`tel:${sanitizePhoneForDialing(phone)}`).catch((error) => {
              debug('UnconfirmedHoursCard: tel: link unsupported', { error });
            });
          }}
        >
          <AppText style={styles.phone}>{phone}</AppText>
        </TouchableOpacity>
      )}
    </View>
  );
}
